import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  styled,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import * as yup from "yup";

import {
  BulkInviteResults,
  InvitableUserTenantRole,
  UserSubaccountRole,
} from "~frontend/__generated__/graphql";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import theme from "~frontend/theme";
import { plural } from "~frontend/util/plural";

import { FlexBox } from "../FlexBox";
import FormikSwitchField from "../forms/FormikSwitchField";

const NoBulletList = styled("ul")({
  listStyleType: "none",
  li: {
    paddingBottom: theme.spacing(1),
  },
});

const MAX_INVITE_EMAILS = 100;

export type Values<
  RoleType extends UserSubaccountRole | InvitableUserTenantRole,
> = {
  fullName: string;
  email: string;
  emails?: string[];
  role: RoleType;
};

function pullEmailsFromString(emailStr: string) {
  const emailSchema = yup.string().email();

  return Array.from(
    new Set(
      emailStr
        .split(",")
        .map(em => em.trim())
        .filter(em => em.length)
        .filter(em => emailSchema.isValidSync(em))
    )
  );
}

function BulkInviteResultsDialog({
  bulkInviteResults,
  onClose,
}: {
  bulkInviteResults: BulkInviteResults;
  onClose: () => void;
}): ReactElement {
  return (
    <Dialog maxWidth="sm" fullWidth open={true}>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        <Card sx={{ mt: 2 }}>
          <CardHeader title="Invite Users Summary" />
          <CardContent>
            <Typography variant="body1" sx={{ pb: 1 }}>
              {plural(
                bulkInviteResults.nSuccess,
                "successful user invitation",
                "successful user invitations"
              )}
            </Typography>
            <Typography variant="body1" sx={{ pb: 1 }}>
              {plural(
                bulkInviteResults.nFailed,
                "failed user invitation",
                "failed user invitations"
              )}
            </Typography>
            {bulkInviteResults.roleExists.length ? (
              <NoBulletList>
                <Typography
                  variant="body2"
                  component="li"
                  key="bulk-invite-role-exists"
                >
                  {plural(
                    bulkInviteResults.roleExists.length,
                    "email already exists ",
                    "emails already exist "
                  )}
                  with this role in this organization
                </Typography>
                {bulkInviteResults.roleExists.map((email, idx) => (
                  <Typography
                    variant="body3"
                    component="li"
                    key={`bulk-role-email-exists-${idx}`}
                  >
                    {email}
                  </Typography>
                ))}
              </NoBulletList>
            ) : null}
            {bulkInviteResults.inviteExists.length ? (
              <NoBulletList>
                <Typography
                  variant="body2"
                  component="li"
                  key="bulk-invite-email-exists"
                >
                  {plural(
                    bulkInviteResults.inviteExists.length,
                    "email has ",
                    "emails have "
                  )}
                  already been sent invites for this role in this organization
                </Typography>
                {bulkInviteResults.inviteExists.map((email, idx) => (
                  <Typography
                    variant="body3"
                    component="li"
                    key={`bulk-invite-email-exists-${idx}`}
                  >
                    {email}
                  </Typography>
                ))}
              </NoBulletList>
            ) : null}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ pt: 2, pb: 3, px: 3 }}>
        <Button onClick={onClose} variant="contained" color="tertiary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InviteDialog<
  RoleType extends UserSubaccountRole | InvitableUserTenantRole,
>({
  open,
  onClose,
  onSubmit,
  roleOptions,
  defaultRole,
  enableMultipleInvites = false,
  bulkInviteResults,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Values<RoleType>) => Promise<void>;
  roleOptions: { value: RoleType; label: string }[];
  defaultRole: RoleType;
  enableMultipleInvites: boolean;
  bulkInviteResults?: BulkInviteResults | null;
}): ReactElement {
  return bulkInviteResults ? (
    <BulkInviteResultsDialog
      bulkInviteResults={bulkInviteResults}
      onClose={onClose}
    />
  ) : (
    <FormikDialog
      maxWidth="sm"
      fullWidth
      open={open}
      initialValues={{
        fullName: "",
        email: "",
        emails: "",
        role: defaultRole,
        multipleInvitesEnabled: false,
      }}
      onSubmit={async (values, formik) => {
        if (values.multipleInvitesEnabled) {
          // extract the first 100 valid email addresses from the given string
          const emailArray = pullEmailsFromString(values.emails).slice(
            0,
            MAX_INVITE_EMAILS
          );

          await onSubmit({
            ...values,
            emails: emailArray,
          });
        } else {
          await onSubmit({ ...values, emails: undefined });
          formik.resetForm();
          onClose();
        }
      }}
      validationSchema={yup.object().shape({
        multipleInvitesEnabled: yup.boolean().required("Required"),
        fullName: yup.string().when("multipleInvitesEnabled", {
          is: true,
          then: schema => schema.notRequired(),
          otherwise: schema => schema.required("Required"),
        }),
        email: yup.string().when("multipleInvitesEnabled", {
          is: true,
          then: schema => schema.notRequired(),
          otherwise: schema =>
            schema.required("Required").email("Invalid email"),
        }),
        emails: yup.string().when("multipleInvitesEnabled", {
          is: true,
          then: schema =>
            schema.required().test("email-count", emailStr => {
              if (emailStr) {
                const emailArray = pullEmailsFromString(emailStr);
                return (
                  emailArray.length > 0 &&
                  emailArray.length <= MAX_INVITE_EMAILS
                );
              }
              return true;
            }),

          otherwise: schema => schema.notRequired(),
        }),
        role: yup.string().required("Required"),
      })}
    >
      {({ isSubmitting, values }) => (
        <>
          <DialogTitle>
            <FlexBox justifyContent="space-between">
              Invite User
              {enableMultipleInvites && (
                <FormikSwitchField
                  name="multipleInvitesEnabled"
                  label="Invite Multiple Users"
                  switchProps={{
                    disabled: isSubmitting,
                  }}
                />
              )}
            </FlexBox>
          </DialogTitle>
          <DialogContent>
            <>
              <FormikErrorBanner />
              {values.multipleInvitesEnabled ? (
                <FormikTextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={3}
                  name="emails"
                  label="Emails"
                  helperText="Enter a comma separated list of up to 100 email addresses. All invites will be assigned the role specified below."
                  disabled={isSubmitting}
                />
              ) : (
                <>
                  <FormikTextField
                    fullWidth
                    label="Full Name"
                    name="fullName"
                  />
                  <FormikTextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                  />
                </>
              )}

              <FormikSelect
                fullWidth
                name="role"
                label="Role"
                disabled={roleOptions.length === 1 || isSubmitting}
              >
                {roleOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </FormikSelect>
            </>
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} onClick={onClose} color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              color={"primary"}
              variant="contained"
            >
              {isSubmitting ? "Sending..." : "Send Invite"}
            </Button>
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
}
